import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Plus, Minus, ShoppingBag, CreditCard, Wallet, Truck, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });

  useEffect(() => {
    loadCart();
    trackCheckout();
  }, []);

  const loadCart = () => {
    const saved = localStorage.getItem('indicraft_cart');
    if (saved) {
      const items = JSON.parse(saved);
      if (items.length === 0) {
        navigate('/shop');
      }
      setCartItems(items);
    } else {
      navigate('/shop');
    }
  };

  const trackCheckout = async () => {
    try {
      await axios.post(`${API_URL}/api/analytics/track`, {
        event_type: 'checkout_initiated',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
  };

  const updateQuantity = (productId, delta) => {
    const newCart = cartItems.map(item => {
      if (item.id === productId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
  };

  const removeItem = (productId) => {
    const newCart = cartItems.filter(item => item.id !== productId);
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
    if (newCart.length === 0) {
      navigate('/shop');
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.discount_price * item.quantity), 0);
  const shipping = subtotal >= 499 ? 0 : 50;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const validateForm = () => {
    const required = ['customer_name', 'customer_email', 'customer_phone', 'address', 'city', 'state', 'pincode'];
    for (const field of required) {
      if (!formData[field]?.trim()) {
        toast.error(`Please fill in ${field.replace('customer_', '').replace('_', ' ')}`);
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.customer_email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!/^\d{10}$/.test(formData.customer_phone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!/^\d{6}$/.test(formData.pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const orderData = {
        ...formData,
        items: cartItems.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.discount_price
        })),
        total_amount: total,
        payment_method: paymentMethod
      };

      const response = await axios.post(`${API_URL}/api/orders`, orderData);
      
      if (paymentMethod === 'online' && response.data.razorpay_order_id) {
        const configRes = await axios.get(`${API_URL}/api/config/razorpay`);
        const options = {
          key: configRes.data.key_id,
          amount: total * 100,
          currency: 'INR',
          name: 'Indicraft',
          description: 'Sacred Puja Essentials',
          order_id: response.data.razorpay_order_id,
          handler: async function (razorpayResponse) {
            try {
              await axios.post(`${API_URL}/api/orders/verify-payment`, {
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
                order_id: response.data.id
              });
              localStorage.removeItem('indicraft_cart');
              navigate(`/order-confirmation/${response.data.id}`);
            } catch (error) {
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            name: formData.customer_name,
            email: formData.customer_email,
            contact: formData.customer_phone
          },
          theme: {
            color: '#800000'
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        localStorage.removeItem('indicraft_cart');
        navigate(`/order-confirmation/${response.data.id}`);
      }
    } catch (error) {
      console.error('Order creation failed:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)} />
      
      <main className="flex-grow py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-maroon hover:text-maroon-dark mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Shopping
          </button>

          <h1 className="text-3xl md:text-4xl font-heading font-bold text-maroon mb-8">
            Checkout
          </h1>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Contact Info */}
                <div className="bg-white rounded-2xl p-6 border border-gold/10">
                  <h2 className="font-heading font-semibold text-maroon text-xl mb-4">Contact Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-maroon/70 mb-1">Full Name *</label>
                      <Input
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-maroon/70 mb-1">Email *</label>
                      <Input
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-maroon/70 mb-1">Phone Number *</label>
                      <Input
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                        placeholder="10-digit mobile number"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white rounded-2xl p-6 border border-gold/10">
                  <h2 className="font-heading font-semibold text-maroon text-xl mb-4">Shipping Address</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-maroon/70 mb-1">Street Address *</label>
                      <Input
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                        placeholder="House/Flat No., Street Name"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-maroon/70 mb-1">City *</label>
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                          placeholder="City"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-maroon/70 mb-1">State *</label>
                        <Input
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                          placeholder="State"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-maroon/70 mb-1">Pincode *</label>
                        <Input
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          className="h-12 rounded-lg border-maroon/20 focus:border-maroon"
                          placeholder="6-digit pincode"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white rounded-2xl p-6 border border-gold/10">
                  <h2 className="font-heading font-semibold text-maroon text-xl mb-4">Payment Method</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('cod')}
                      className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        paymentMethod === 'cod'
                          ? 'border-maroon bg-maroon/5'
                          : 'border-gray-200 hover:border-maroon/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        paymentMethod === 'cod' ? 'bg-maroon text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Wallet className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-maroon">Cash on Delivery</p>
                        <p className="text-sm text-maroon/60">Pay when you receive</p>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('online')}
                      className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        paymentMethod === 'online'
                          ? 'border-maroon bg-maroon/5'
                          : 'border-gray-200 hover:border-maroon/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        paymentMethod === 'online' ? 'bg-maroon text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <CreditCard className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-maroon">Pay Online</p>
                        <p className="text-sm text-maroon/60">Cards, UPI, Net Banking</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6 border border-gold/10 sticky top-24">
                  <h2 className="font-heading font-semibold text-maroon text-xl mb-4">Order Summary</h2>
                  
                  <div className="space-y-4 max-h-[300px] overflow-y-auto mb-4">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex gap-3 pb-4 border-b border-gray-100">
                        <img
                          src={item.images?.[0] || 'https://via.placeholder.com/80'}
                          alt={item.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-maroon text-sm line-clamp-1">{item.name}</h3>
                          <p className="text-maroon/60 text-sm">₹{item.discount_price} × {item.quantity}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, -1)}
                              className="w-6 h-6 rounded-full bg-cream flex items-center justify-center"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-sm font-medium">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, 1)}
                              className="w-6 h-6 rounded-full bg-cream flex items-center justify-center"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="ml-auto text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 py-4 border-t border-gray-100">
                    <div className="flex justify-between text-maroon/70">
                      <span>Subtotal</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-maroon/70">
                      <span>Shipping</span>
                      <span>{shipping === 0 ? 'FREE' : `₹${shipping}`}</span>
                    </div>
                    {subtotal < 499 && (
                      <p className="text-xs text-saffron">Add ₹{(499 - subtotal).toFixed(0)} more for free shipping!</p>
                    )}
                  </div>

                  <div className="flex justify-between font-bold text-lg text-maroon pt-4 border-t border-gray-100">
                    <span>Total</span>
                    <span>₹{total.toFixed(2)}</span>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-maroon hover:bg-maroon-dark text-white font-semibold py-6 rounded-full mt-6 text-lg"
                    data-testid="place-order-btn"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </div>
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5 mr-2" />
                        {paymentMethod === 'cod' ? 'Place Order' : 'Pay Now'}
                      </>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-4 mt-4 text-maroon/60 text-xs">
                    <div className="flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      <span>Secure Checkout</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Truck className="w-4 h-4" />
                      <span>Fast Delivery</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
